import { useState } from "react";
import { Stack } from "@mui/material";
import { PageProps } from "pages/_app";
import { EligibilityCheckType } from "tanstack/ivr/query-options";
import VatReturnCard, { VatReturnCardProps } from "components/v2/templates/instant-vat/vat_return_card/VatReturnCard";
import AppSectionAccordion, {
    AppSectionAccordionProps,
} from "components/v2/organisms/app-section-accordion/AppSectionAccordion";

export interface VatReturnsAndRefund {
    position: "payment" | "repayment";
    vat_return_period_key: string;
    vat_return_period_start: string;
    vat_return_period_end: string;
    vat_return_due_date?: string | null;
    status:
        | "upcoming"
        | "awaiting for submission"
        | "adsum processing"
        | "hmrc processing"
        | "hmrc/adsum processing"
        | "repaid by hmrc"
        | "adsum funded"
        | "being repaid by hmrc"
        | "awaiting payment confirmation";

    adsum_fees?: number | null;
    adsum_fees_rate?: number | null;
    funded_amount?: number | null;
    funded_date?: string | null;
    hmrc_paid_out_date?: string | null;
    hmrc_approved_date?: string | null;
    outstanding_amount?: number | null;
    vat_amount?: number | null;
    vat_return_submission_date?: string | null;
    is_eligible_for_funding?: boolean | null;
    offset?: number | null;
    liability_zero_at?: string | null;
    dilutions_check?: boolean | null;
    monthly_fixed_fee?: number | null;
    compliance_check?: boolean | null;
    paid_at_adsum_bank?: boolean | null;
}

export interface VatReturnsAndRefundsProps {
    pageProps: PageProps;
    items: VatReturnsAndRefund[];
    eligibilityCheck?: EligibilityCheckType;
    onClick: (vatReturn: VatReturnsAndRefund) => void;
    demo?: boolean;
}

interface VatReturnCategory {
    current: VatReturnsAndRefund[];
    recent: VatReturnsAndRefund[];
    upcoming: VatReturnsAndRefund[];
    settled: VatReturnsAndRefund[];
}

function VatReturnsAndRefunds(props: VatReturnsAndRefundsProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const priorityOrder: (keyof VatReturnCategory)[] = ["current", "recent", "upcoming", "settled"];

    const methods = {
        filterReturnsByCategories() {
            const categories: VatReturnCategory = {
                current: [],
                recent: [],
                upcoming: [],
                settled: [],
            };

            for (const item of props.items) {
                const { status } = item;

                if (item.position === "payment") {
                    categories.settled.push(item);
                } else if (
                    status === "awaiting for submission" ||
                    status === "adsum processing" ||
                    status === "hmrc processing" ||
                    status === "hmrc/adsum processing" ||
                    status === "awaiting payment confirmation" ||
                    status === "being repaid by hmrc"
                ) {
                    categories.current.push(item);
                } else if (status === "adsum funded" && (item.outstanding_amount || 0) > 0) {
                    categories.recent.push(item);
                } else if (status === "upcoming") {
                    categories.upcoming.push(item);
                } else {
                    categories.settled.push(item);
                }
            }

            return categories;
        },
        getTopFilledCategory(categories: VatReturnCategory) {
            for (const category of priorityOrder) {
                if (categories[category].length > 0) {
                    return category;
                }
            }

            return null;
        },
        getRemainingCategories(topCategory: keyof VatReturnCategory | null): (keyof VatReturnCategory)[] {
            if (topCategory == null) {
                return [];
            }

            return priorityOrder.slice(priorityOrder.indexOf(topCategory) + 1);
        },
    };

    const stackProps = {
        sx: {
            gap: 3,
        },
    };

    const categories = methods.filterReturnsByCategories();
    const topCategory = methods.getTopFilledCategory(categories);
    const remainingCategories = methods.getRemainingCategories(topCategory);
    const topCategoryItems = topCategory ? categories[topCategory] : [];
    const remainingCategoriesItems: VatReturnsAndRefund[] = [];

    for (const category of remainingCategories) {
        remainingCategoriesItems.push(...(categories[category] || []));
    }

    const topCategoryVatReturnsProps: VatReturnsAndRefundsSectionProps = {
        items: topCategoryItems,
        onClick: props.onClick,
        pageProps: props.pageProps,
        eligibilityCheck: props.eligibilityCheck,
        demo: props.demo,
    };

    const remainingCategoriesVatReturnsProps: VatReturnsAndRefundsSectionProps = {
        items: remainingCategoriesItems,
        onClick: props.onClick,
        pageProps: props.pageProps,
        eligibilityCheck: props.eligibilityCheck,
        demo: props.demo,
    };

    const hasRemainingCategories = remainingCategoriesItems.length > 0;

    const bottomSectionAccordionProps: AppSectionAccordionProps = {
        title: isExpanded ? "Hide InstantVAT refunds" : "View all InstantVAT refunds",
        titleTextProps: {
            sx: {
                fontSize: 14,
                fontWeight: 500,
                color: "#7C8593",
            },
        },
        accordionProps: {
            expanded: isExpanded,
            sx: {
                ".MuiButtonBase-root.MuiAccordionSummary-root": {
                    justifySelf: "center",
                },
            },
            onChange: (event, expanded) => {
                setIsExpanded(expanded);
            },
        },
        accordionDetailsProps: {
            sx: {
                pt: 3,
            },
        },
    };

    return (
        <Stack {...stackProps}>
            <VatReturnsAndRefundsSection {...topCategoryVatReturnsProps} />
            {hasRemainingCategories && (
                <AppSectionAccordion {...bottomSectionAccordionProps}>
                    <VatReturnsAndRefundsSection {...remainingCategoriesVatReturnsProps} />
                </AppSectionAccordion>
            )}
        </Stack>
    );
}

export default VatReturnsAndRefunds;

interface VatReturnsAndRefundsSectionProps {
    pageProps: PageProps;
    items: VatReturnsAndRefund[];
    eligibilityCheck?: EligibilityCheckType;
    onClick: (vatReturn: VatReturnsAndRefund) => void;
    demo?: boolean;
}

function VatReturnsAndRefundsSection(props: VatReturnsAndRefundsSectionProps) {
    const methods = {
        renderCards() {
            return props.items.map((vatReturn, index) => {
                const vatReturnCardProps: VatReturnCardProps = {
                    vatReturn,
                    pageProps: props.pageProps,
                    eligibilityCheck: props.eligibilityCheck,
                    onClick: () => props.onClick(vatReturn),
                    demo: props.demo,
                };

                return <VatReturnCard key={vatReturn.vat_return_period_key + index} {...vatReturnCardProps} />;
            });
        },
    };

    const stackProps = {
        sx: {
            gap: 3,
        },
    };

    return <Stack {...stackProps}>{methods.renderCards()}</Stack>;
}
